import type { OpenAPI } from "simplydo/interfaces";

import SearchParams from "./searchParams";

export type NotificationContext = Record<
  string,
  string | number | boolean | Record<string, never> | undefined
>;

/** Runtime event shape used for notification navigation. */
export type NotificationEvent = {
  user?: string;
  organisation?: string;
  comment?: string;
  commentId?: string;
  commentText?: string;
  isFollower?: boolean;
  message?: string;
  createdAt?: string;
  achievement?: string;
  invitation?: string;
  threadId?: string;
  messageId?: string;
  idea?: string;
  challenge?: string;
  group?: string;
  discussion?: string;
  calendar?: string;
  submittedChallengeId?: string;
};

/** Broader notification shape for navigation than the OpenAPI oneOf schema. */
export type NotificationNavSource = {
  notificationType?: string;
  events?: NotificationEvent[];
  idea?: string;
  challenge?: string;
  group?: string;
  discussion?: string;
  organisation?: string;
  ideaBusinessProfile?: string;
  ownerChallenge?: { _id?: string };
  context?: NotificationContext;
};

export type Notification = NotificationNavSource;
export type OpenAPINotification = OpenAPI.Schemas["Notification"];

type NotificationLocationOptions = { pathname?: string };

const MENTIONED_ENTITY_PATHS: Record<
  string,
  { segment: string; id: "discussion" | "idea" }
> = {
  mentionedDiscussionComment: { segment: "discussions", id: "discussion" },
  mentionedIdeaComment: { segment: "ideas", id: "idea" },
};

const SOURCE_ID_FIELDS = [
  "idea",
  "challenge",
  "group",
  "discussion",
  "ideaBusinessProfile",
] as const;

function parseJsonField<T>(value: unknown): T | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as T;
    } catch {
      return undefined;
    }
  }
  return value as T;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function pathWithSearch(
  path: string,
  params: Record<string, string | undefined>,
) {
  return `${path}${new SearchParams(params).toSafeString()}`;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

type SourceOrEventIdKey =
  | "idea"
  | "challenge"
  | "group"
  | "discussion"
  | "organisation";

/** Prefer the event value when present; only resolve if the key exists on source. */
function idFromSourceOrEvent(
  source: NotificationNavSource,
  event: NotificationEvent,
  key: SourceOrEventIdKey,
): string | undefined {
  if (!(key in source)) {
    const keyTitleCase = key.charAt(0).toUpperCase() + key.slice(1);
    const ownerKey = `owner${keyTitleCase}`;
    if (ownerKey in source) {
      return asString(
        (source[ownerKey as keyof NotificationNavSource] as { _id?: string })
          ?._id,
      );
    }
    if (ownerKey in event) {
      return asString(
        (event[ownerKey as keyof NotificationEvent] as { _id?: string })?._id,
      );
    }
    return undefined;
  }
  return asString(key in event ? event[key] : source[key]);
}

function resolveNavIds(
  source: NotificationNavSource,
  event: NotificationEvent = {},
) {
  return {
    organisationId: idFromSourceOrEvent(source, event, "organisation"),
    ideaId: idFromSourceOrEvent(source, event, "idea"),
    challengeId: idFromSourceOrEvent(source, event, "challenge"),
    groupId: idFromSourceOrEvent(source, event, "group"),
    discussionId: idFromSourceOrEvent(source, event, "discussion"),
    threadId: asString(event.threadId),
    commentId: asString(event.commentId),
    calendarId: asString(event.calendar),
    submittedChallengeId: asString(event.submittedChallengeId),
  };
}

function resolveIds(source: NotificationNavSource) {
  const events = source.events ?? [];
  return { events, ...resolveNavIds(source, events[0] ?? {}) };
}

export function parseNotificationNavSource(
  data: unknown,
): NotificationNavSource | null {
  const record = asRecord(data);
  if (
    !record?.notificationType ||
    typeof record.notificationType !== "string"
  ) {
    return null;
  }

  const source: NotificationNavSource = {
    notificationType: record.notificationType,
    events: parseJsonField<NotificationEvent[]>(record.events) ?? [],
  };

  for (const key of SOURCE_ID_FIELDS) {
    const value = record[key];
    if (typeof value === "string" && value) {
      source[key] = value;
    }
  }

  const context = parseJsonField<NotificationContext>(record.context);
  if (context) {
    source.context = context;
  }

  const ownerChallenge = parseJsonField<{ _id?: string }>(
    record.ownerChallenge,
  );
  if (ownerChallenge) {
    source.ownerChallenge = ownerChallenge;
  }

  if (!Array.isArray(source.events)) {
    source.events = [];
  }

  return source;
}

export type NotificationLocation = {
  forType: string;
  forId: string | undefined;
  href: string | undefined;
};

export function getNotificationLocation(
  source: NotificationNavSource,
  options: NotificationLocationOptions = {},
): NotificationLocation | null {
  const { notificationType } = source;
  if (!notificationType) return null;

  const {
    events,
    ideaId,
    challengeId,
    groupId,
    discussionId,
    threadId,
    commentId,
    calendarId,
    organisationId,
    submittedChallengeId,
  } = resolveIds(source);

  switch (notificationType) {
    case "messaged":
      return {
        forType: "message",
        forId: threadId,
        href: threadId ? `/messages/${threadId}` : undefined,
      };

    case "ideaBusinessProfileInvited":
      return {
        forType: "ideaBusinessProfile",
        forId: source.ideaBusinessProfile,
        href: pathWithSearch(options.pathname ?? "", {
          businessProfileId: source.ideaBusinessProfile,
        }),
      };

    case "ideaCommented":
    case "ideaCommentReply":
    case "reactedIdeaComment":
      return {
        forType: "idea",
        forId: ideaId,
        href: ideaId
          ? pathWithSearch(`/ideas/${ideaId}`, { comment: commentId })
          : undefined,
      };

    case "collaboratorAdded":
    case "ideaVoted":
    case "ideaInvited":
    case "aiIdeaFeedback":
    case "aiIdeaAnonymityCheck":
      return {
        forType: "idea",
        forId: ideaId,
        href: ideaId ? `/ideas/${ideaId}` : undefined,
      };

    case "stageChanged":
    case "closingSoon":
    case "challengeClosed":
    case "requestOpenChallenge":
    case "challengeTranslate":
      return {
        forType: "challenge",
        forId: challengeId,
        href: challengeId ? `/challenges/${challengeId}` : undefined,
      };

    case "aiChallengeFormat":
      return {
        forType: "challenge",
        forId: challengeId,
        href: challengeId
          ? `/challenges/${challengeId}/settings/format`
          : undefined,
      };

    case "challengeInnointelSearch":
      if (source.context?.searchId) {
        return {
          forType: "innointelSearch",
          forId: String(source.context.searchId),
          href: `/innointel/searches/${source.context.searchId}`,
        };
      }
      return {
        forType: "challenge",
        forId: challengeId,
        href: challengeId ? `/challenges/${challengeId}` : undefined,
      };

    case "challengeAddedToCalendarEvent":
      return challengeId
        ? {
            forType: "challenge",
            forId: challengeId,
            href: challengeId
              ? `/challenges/${challengeId}/planner`
              : undefined,
          }
        : null;

    case "groupAddedToCalendarEvent":
      if (!groupId) return null;
      return calendarId
        ? {
            forType: "group",
            forId: groupId,
            href: calendarId
              ? `/groups/${groupId}/calendars/${calendarId}`
              : undefined,
          }
        : { forType: "group", forId: groupId, href: `/groups/${groupId}` };

    case "assessorAssigned":
      return challengeId
        ? {
            forType: "challenge",
            forId: challengeId,
            href: challengeId
              ? `/challenges/${challengeId}/assessments`
              : undefined,
          }
        : null;

    case "ideaSubmitted":
    case "ideaDeadlineSet":
    case "ideaDeadlineApproaching":
      if (events.length > 1) {
        return {
          forType: "challenge",
          forId: submittedChallengeId,
          href: submittedChallengeId
            ? `/challenges/${submittedChallengeId}/ideas`
            : undefined,
        };
      }
      return {
        forType: "idea",
        forId: ideaId,
        href: ideaId ? `/ideas/${ideaId}` : undefined,
      };

    case "reactedDiscussionComment":
    case "discussionCommentReply":
    case "discussionPostRequiresApproval":
    case "discussionPosted":
    case "reactedGroupDiscussion":
    case "repliedGroupDiscussion":
    case "mentionedGroupDiscussion":
    case "discussionPollVoted":
      if (groupId) {
        if (discussionId) {
          return {
            forType: "group",
            forId: groupId,
            href: pathWithSearch(
              `/groups/${groupId}/discussions/${discussionId}`,
              { comment: commentId },
            ),
          };
        }
        return { forType: "group", forId: groupId, href: `/groups/${groupId}` };
      }
      if (discussionId) {
        return {
          forType: "discussion",
          forId: discussionId,
          href: pathWithSearch(`/discussions/${discussionId}`, {
            comment: commentId,
          }),
        };
      }
      return null;
    case "mentionedDiscussionComment":
    case "mentionedIdeaComment": {
      const mentioned = MENTIONED_ENTITY_PATHS[notificationType];
      const entityId = mentioned ? source[mentioned.id] : undefined;
      if (!mentioned || !entityId || typeof entityId !== "string") {
        return null;
      }
      return {
        forType: groupId ? "group" : "discussion",
        forId: groupId ? groupId : discussionId,
        href: pathWithSearch(`/${mentioned.segment}/${entityId}`, {
          comment: commentId,
        }),
      };
    }

    case "mentionedIdeaProjectComment":
    case "projectIdeaCommented":
    case "projectIdeaReplied":
      return challengeId
        ? {
            forType: "challenge",
            forId: challengeId,
            href: pathWithSearch(`/challenges/${challengeId}/board`, {
              idea: ideaId,
              comment: commentId,
            }),
          }
        : null;

    case "projectIdeaMoved":
    case "projectAssignedAssignee":
    case "projectAssignedManager":
      return challengeId
        ? {
            forType: "challenge",
            forId: challengeId,
            href: pathWithSearch(`/challenges/${challengeId}/board`, {
              idea: ideaId,
            }),
          }
        : null;

    case "unlockedAchievement":
      return { forType: "user", forId: undefined, href: "/achievements" };

    case "pollClosed":
      return source.context?.pollId
        ? {
            forType: "organisation",
            forId: String(organisationId),
            href: `/admin/polls/${source.context.pollId}/results`,
          }
        : null;

    case "groupJoinRequested":
    case "groupInvited":
      return { forType: "user", forId: undefined, href: "/" };

    case "groupUserJoined":
      return groupId
        ? { forType: "group", forId: groupId, href: `/groups/${groupId}` }
        : null;

    case "announcementReport":
      return typeof source.context?.logLink === "string"
        ? { forType: "user", forId: undefined, href: source.context.logLink }
        : null;

    default:
      return null;
  }
}
